import { asClass, asFunction, createContainer } from "awilix";
import { BaseApi } from "~/implementations/BaseApi";
import type { IApi } from "./interfaces/IApi";
import { AuthService } from "./implementations/Services/AuthService";
import { UserService } from "./implementations/Services/UserService";
import { RoleService } from "./implementations/Services/RoleService";
import { AccountService } from "./implementations/Services/AccountService";
import { AppNotificationService } from "./implementations/Services/AppNotificationService";
import { DraftReportService } from "./implementations/Services/DraftReportService";
import { LostAndFoundService } from "./implementations/Services/LostAndFoundService";
import { MediaService } from "./implementations/Services/MediaService";
import { ReportCategoryService } from "./implementations/Services/ReportCategoryService";
import { ReportSubcategoryService } from "./implementations/Services/ReportSubcategoryService";
import { RespondingAgencyService } from "./implementations/Services/RespondingAgencyService";
import { ReportService } from "./implementations/Services/ReportService";
import type { IAccountService } from "@saif-reporting/domain/interfaces/services/IAccountService.js";
import type { IAppNotificationService } from "@saif-reporting/domain/interfaces/services/IAppNotificationService.js";
import type { IAuthService } from "@saif-reporting/domain/interfaces/services/IAuthService.js";
import type { IDraftService } from "@saif-reporting/domain/interfaces/services/IDraftService.js";
import type { ILostAndFoundService } from "@saif-reporting/domain/interfaces/services/ILostAndFoundService.js";
import type { IMediaService } from "@saif-reporting/domain/interfaces/services/IMediaService.js";
import type { IReportCategoryService } from "@saif-reporting/domain/interfaces/services/IReportCategoryService.js";
import type { IReportSubcategoryService } from "@saif-reporting/domain/interfaces/services/IReportSubcategoryService.js";
import type { IReportService } from "@saif-reporting/domain/interfaces/services/IReportService.js";
import type { IRespondingAgencyService } from "@saif-reporting/domain/interfaces/services/IRespondingAgencyService.js";
import type { IRoleService } from "@saif-reporting/domain/interfaces/services/IRoleService.js";
import type { IUserService } from "@saif-reporting/domain/interfaces/services/IUserService.js";
import { ContactService } from "./implementations/Services/ContactService";
import type { IContactService } from "@saif-reporting/domain/interfaces/services/IContactService.js";
import type { IMaintenanceIssueService } from "@saif-reporting/domain/interfaces/services/IMaintenanceIssueService.js";
import { MaintenanceIssueService } from "./implementations/Services/MaintenanceIssueService";
import type { ISharedUrlService } from "@saif-reporting/domain/interfaces/services/ISharedUrlService.js";
import { SharedUrlService } from "./implementations/Services/SharedUrlService";
import type { IDashboardAnalyticsService } from "@saif-reporting/domain/interfaces/services/IDashboardAnalyticsService.js";
import { DashboardAnalyticsService } from "./implementations/Services/DashboardAnalyticsService";
import { AuthPayload } from "@saif-reporting/domain/dtos/AuthPayload.js";
import { useAuthStore } from "./store/useAuthStore";
import { ReportPolicy } from "@saif-reporting/domain/policies/ReportPolicy.js";
import { UserPolicy } from "@saif-reporting/domain/policies/UserPolicy.js";
import { AnnouncementPolicy } from "@saif-reporting/domain/policies/AnnouncementPolicy.js";
import type { IPushNotificationService } from "@saif-reporting/domain/interfaces/services/IPushNotificationService.js";
import { PushNotificationService } from "./implementations/Services/PushNotificationService";
import type { IAnnoucementService } from "@saif-reporting/domain/interfaces/services/IAnnoucementService.js";
import { AnnoucementService } from "./implementations/Services/AnnoucementService";

export type ContainerType = {
  baseApi: IApi;
  accountService: IAccountService;
  announcementService: IAnnoucementService;
  appNotificationService: IAppNotificationService;
  authService: IAuthService;
  contactService: IContactService;
  dashboardAnalyticsService: IDashboardAnalyticsService;
  draftReportService: IDraftService;
  lostAndFoundService: ILostAndFoundService;
  mediaService: IMediaService;
  maintenanceIssueService: IMaintenanceIssueService;
  pushNotificationService: IPushNotificationService;
  reportCategoryService: IReportCategoryService;
  reportService: IReportService;
  reportSubcategoryService: IReportSubcategoryService;
  respondingAgencyService: IRespondingAgencyService;
  roleService: IRoleService;
  sharedUrlService: ISharedUrlService;
  userService: IUserService;
  user: AuthPayload | null;

  // resource policies
  reportPolicy: ReportPolicy;
  userPolicy: UserPolicy;
  announcementPolicy: AnnouncementPolicy;
};

export const initContainer = () => {
  const container = createContainer<ContainerType>({});
  container.register({
    baseApi: asClass(BaseApi).singleton(),
    accountService: asClass(AccountService).singleton(),
    announcementService: asClass(AnnoucementService).singleton(),
    appNotificationService: asClass(AppNotificationService).singleton(),
    authService: asClass(AuthService).singleton(),
    contactService: asClass(ContactService).singleton(),
    dashboardAnalyticsService: asClass(DashboardAnalyticsService).singleton(),
    draftReportService: asClass(DraftReportService).singleton(),
    lostAndFoundService: asClass(LostAndFoundService).singleton(),
    maintenanceIssueService: asClass(MaintenanceIssueService).singleton(),
    mediaService: asClass(MediaService).singleton(),
    pushNotificationService: asClass(PushNotificationService).singleton(),
    reportCategoryService: asClass(ReportCategoryService).singleton(),
    reportService: asClass(ReportService).singleton(),
    reportSubcategoryService: asClass(ReportSubcategoryService).singleton(),
    respondingAgencyService: asClass(RespondingAgencyService).singleton(),
    roleService: asClass(RoleService).singleton(),
    sharedUrlService: asClass(SharedUrlService).singleton(),
    userService: asClass(UserService).singleton(),
    user: asFunction(() => {
      const authStore = useAuthStore();
      if (!authStore.user) {
        return null;
      }
      return AuthPayload.create({
        id: authStore.user.id,
        fullname: authStore.user.fullname,
        username: authStore.user.username,
        designation: authStore.user.designation,
        email: authStore.user.email,
        Permissions: authStore.user.Permissions,
        roles: authStore.user.roles,
        maxReportingLevel: authStore.user.maxReportingLevel,
      });
    }).transient(),

    // resource policies
    reportPolicy: asClass(ReportPolicy).transient(),
    userPolicy: asClass(UserPolicy).transient(),
    announcementPolicy: asClass(AnnouncementPolicy).transient(),
  });

  return container;
};
